import "./bootstrap";
import { createApp, defineAsyncComponent, h } from "vue";
import axios from 'axios';

// Global axios interceptor: attach impersonated client ID header if set
const IMPERSONATE_KEY = 'anthemfx_impersonated_client_id';
axios.interceptors.request.use(config => {
	const clientId = localStorage.getItem(IMPERSONATE_KEY);
	if (clientId) {
		config.headers['X-Impersonate-Client'] = clientId;
	}
	return config;
});

/**
 * Mount a component lazily: only loads the JS when the target element exists
 * on the page. This keeps peak memory low during build (Rollup processes
 * smaller independent chunks) and speeds up page loads.
 *
 * Props are extracted from the component tag's attributes in the blade
 * template (e.g. <collection-item id="37"> → { id: 37 }).  Attributes
 * prefixed with ":" are parsed as JS expressions; plain attributes are
 * passed as strings unless they look numeric.
 */
function mountIfExists(selector, name, loader, setup) {
	const el = document.getElementById(selector.replace('#', ''));
	if (!el) return;

	// Extract props from the first child element (the component tag in the blade template)
	const tag = el.querySelector(name);
	const props = {};
	if (tag) {
		for (const attr of tag.attributes) {
			let key = attr.name;
			let val = attr.value;
			if (key.startsWith(':')) {
				// Vue-style binding — evaluate the expression
				key = key.slice(1);
				try { val = JSON.parse(val); } catch { /* keep as string */ }
			} else if (val !== '' && !isNaN(val)) {
				val = Number(val);
			}
			props[key] = val;
		}
	}

	const AsyncComp = defineAsyncComponent(loader);
	const app = createApp({ render: () => h(AsyncComp, props) });
	if (setup) setup(app);
	app.mount(el);
}

// ─── Page mounts ─────────────────────────────────────────────────────────────
// Each component is only imported when its mount element exists on the page.

mountIfExists('#app', 'contest-creator', () => import('./components/ContestCreator.vue'));
mountIfExists('#prizeslot', 'prizeslotmanager', () => import('./components/PrizeSlotManager.vue'));

mountIfExists('#contest', 'contest', () => import('./components/Contest.vue'), app => {
	app.config.globalProperties.$env = {
		APP_URL: import.meta.env.VITE_APP_URL || window.APP_URL,
		ADMIN_URL: import.meta.env.VITE_ADMIN_URL || window.ADMIN_URL
	};
});

mountIfExists('#teams', 'teams', () => import('./components/Teams.vue'), app => {
	import('./plugins/vue-query').then(m => app.use(m.default));
});

mountIfExists('#accountsettings', 'account-settings', () => import('./components/AccountSettings.vue'));
mountIfExists('#email', 'system-email', () => import('./components/SystemEmailManager.vue'));
mountIfExists('#client', 'client-manager', () => import('./components/ClientManager.vue'));
mountIfExists('#clientImpersonator', 'client-impersonator', () => import('./components/ClientImpersonator.vue'));
mountIfExists('#users', 'users', () => import('./components/Users.vue'));
mountIfExists('#home', 'landing-page', () => import('./components/LandingPage.vue'));
mountIfExists('#user', 'user', () => import('./components/User.vue'));
mountIfExists('#games', 'games', () => import('./components/Games.vue'));
mountIfExists('#players', 'players', () => import('./components/Players.vue'));
mountIfExists('#teamSelector', 'team-selector', () => import('./components/TeamSelector.vue'));
mountIfExists('#collectionItem', 'collection-item', () => import('./components/CollectionItem.vue'));
mountIfExists('#collectionList', 'collection-list', () => import('./components/CollectionList.vue'));
mountIfExists('#assetManager', 'asset-manager', () => import('./components/AssetManager.vue'));
mountIfExists('#contestList', 'contest-list', () => import('./components/ContestList.vue'));
mountIfExists('#sponsors', 'sponsor-list', () => import('./components/SponsorList.vue'));
mountIfExists('#sponsors2', 'sponsors2', () => import('./components/Sponsors2.vue'));
mountIfExists('#stats', 'stat-list', () => import('./components/Stats.vue'));
mountIfExists('#contestDefaults', 'contest-defaults-manager', () => import('./components/ContestDefaultsManager.vue'));
mountIfExists('#clientEmails', 'client-email-page', () => import('./components/ClientEmailPage.vue'));
mountIfExists('#gameDaySummary', 'game-day-summary', () => import('./components/GameDaySummary.vue'));
mountIfExists('#teamDashboard', 'team-dashboard', () => import('./components/TeamDashboard.vue'));
mountIfExists('#clientDashboard', 'client-dashboard', () => import('./components/ClientDashboard.vue'));
mountIfExists('#userReport', 'user-report', () => import('./components/UserReport.vue'));
mountIfExists('#changelog', 'app-changelog', () => import('./components/Changelog.vue'));
mountIfExists('#leagues', 'league-admin', () => import('./components/LeagueAdmin.vue'));
mountIfExists('#couponCodes', 'coupon-code-dashboard', () => import('./components/CouponCodeDashboard.vue'));
mountIfExists('#vendorUsers', 'vendor-user-manager', () => import('./components/VendorUserManager.vue'));
